import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { apiCall } from "utils/apiClient";

function mapProfileDetails(data, fallbackUser) {
  return {
    id: data?.id || fallbackUser?.id || "",
    name: data?.name || fallbackUser?.name || "",
    companyName: data?.company_name || fallbackUser?.companyName || "",
    email: data?.email || fallbackUser?.email || "",
    address: data?.address || fallbackUser?.address || "",
    mobile: data?.mobile || fallbackUser?.mobile || "",
    status: data?.status || fallbackUser?.status || "",
    mobileVerified: data?.mobile_verified || fallbackUser?.mobileVerified || "",
    emailVerified: data?.email_verified || fallbackUser?.emailVerified || "",
    fcmToken: data?.fcm_token || fallbackUser?.fcmToken || "",
    bankDetails: fallbackUser?.bankDetails || undefined,
  };
}

function mapBankDetails(data) {
  return {
    accountHolder: data?.account_holder_name || "",
    bankName: data?.bank_name || "",
    bankBranch: data?.bank_branch || "",
    accountNumber: data?.account_number || "",
    ifsc: data?.ifsc_code || "",
    upiId: data?.upi_id || "",
    bankDocument: data?.bank_document || "",
  };
}

const emptyBankForm = {
  accountHolder: "",
  bankName: "",
  bankBranch: "",
  accountNumber: "",
  ifsc: "",
  upiId: "",
  bankDocument: "",
};

const emptyDetailsForm = {
  name: "",
  companyName: "",
  email: "",
  address: "",
  mobile: "",
  status: "active",
  mobileVerified: "0",
  emailVerified: "0",
  fcmToken: "",
};

const ChannelPartnerEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editChannelPartner = location.state?.editChannelPartner || null;

  const [infoMessage, setInfoMessage] = useState("");
  const [bankMessage, setBankMessage] = useState("");
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingBank, setIsLoadingBank] = useState(false);
  const [isSavingBank, setIsSavingBank] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState(emptyDetailsForm);
  const [bankForm, setBankForm] = useState(emptyBankForm);

  useEffect(() => {
    if (!editChannelPartner?.id) {
      navigate("/channel-partner");
      return;
    }

    setDetailsForm((current) => ({
      ...current,
      name: editChannelPartner?.name || "",
      companyName: editChannelPartner?.company_name || "",
      email: editChannelPartner?.email || "",
      mobile: editChannelPartner?.mobile || "",
      status: editChannelPartner?.status || "active",
      mobileVerified: String(editChannelPartner?.mobile_verified ?? "0"),
      emailVerified: String(editChannelPartner?.email_verified ?? "0"),
    }));
  }, [editChannelPartner, navigate]);

  useEffect(() => {
    let isMounted = true;

    const fetchProfileDetails = async () => {
      if (!editChannelPartner?.id) return;

      setIsLoadingProfile(true);
      setInfoMessage("");

      const response = await apiCall({
        endpoint: `/channel-partner/profile/${editChannelPartner.id}`,
        method: "GET",
      });

      if (!isMounted) return;

      const statusCode = Number(response?.status ?? response?.statusCode ?? response?.code ?? 0);
      const isSuccessful = statusCode === 200 || statusCode === 201 || response?.success === true;

      if (!isSuccessful) {
        setIsLoadingProfile(false);
        if (statusCode >= 400) {
          setInfoMessage(response?.message || "Unable to load profile details.");
        }
        return;
      }

      const nextUser = mapProfileDetails(response?.data || {}, editChannelPartner);
      setDetailsForm({
        name: nextUser.name,
        companyName: nextUser.companyName,
        email: nextUser.email,
        address: nextUser.address,
        mobile: nextUser.mobile,
        status: nextUser.status || "active",
        mobileVerified: String(nextUser.mobileVerified ?? "0"),
        emailVerified: String(nextUser.emailVerified ?? "0"),
        fcmToken: nextUser.fcmToken,
      });
      setIsLoadingProfile(false);
    };

    fetchProfileDetails();

    return () => {
      isMounted = false;
    };
  }, [editChannelPartner?.id]);

  useEffect(() => {
    let isMounted = true;

    const fetchBankDetails = async () => {
      if (!editChannelPartner?.id) return;

      setIsLoadingBank(true);
      setBankMessage("");

      const response = await apiCall({
        endpoint: `/channel-partner/bank-details/${editChannelPartner.id}`,
        method: "GET",
      });

      if (!isMounted) return;

      const statusCode = Number(response?.status ?? response?.statusCode ?? response?.code ?? 0);
      const isSuccessful = statusCode === 200 || statusCode === 201 || response?.success === true;

      if (!isSuccessful) {
        setHasBankDetails(false);
        setBankForm(emptyBankForm);
        setIsLoadingBank(false);
        if (statusCode >= 400 && statusCode !== 404) {
          setBankMessage(response?.message || "Unable to load bank details.");
        }
        return;
      }

      const nextBankDetails = mapBankDetails(response?.data || {});
      setHasBankDetails(
        Boolean(nextBankDetails.accountHolder || nextBankDetails.accountNumber || nextBankDetails.upiId),
      );
      setBankForm(nextBankDetails);
      setIsLoadingBank(false);
    };

    fetchBankDetails();

    return () => {
      isMounted = false;
    };
  }, [editChannelPartner?.id]);

  const updateDetailsField = (key) => (event) => {
    const value = event.target.value;
    setDetailsForm((current) => ({ ...current, [key]: value }));
  };

  const updateBankField = (key) => (event) => {
    const value = event.target.value;
    setBankForm((current) => ({ ...current, [key]: value }));
  };

  const saveDetails = async (event) => {
    event.preventDefault();
    if (!detailsForm.mobile.trim()) {
      setInfoMessage("Mobile number is required.");
      return;
    }

    if (!editChannelPartner?.id) {
      setInfoMessage("Unable to save profile details. Missing channel partner ID.");
      return;
    }

    setIsSavingProfile(true);
    setInfoMessage("");

    const response = await apiCall({
      endpoint: `/channel-partner/profile/${editChannelPartner.id}`,
      method: "PUT",
      data: {
        name: detailsForm.name.trim() || "Channel Partner",
        company_name: detailsForm.companyName.trim(),
        email: detailsForm.email.trim(),
        address: detailsForm.address.trim(),
        mobile: detailsForm.mobile.trim(),
        status: detailsForm.status,
        mobile_verified: detailsForm.mobileVerified,
        email_verified: detailsForm.emailVerified,
        fcm_token: detailsForm.fcmToken.trim(),
      },
    });

    setIsSavingProfile(false);

    const statusCode = Number(response?.status ?? response?.statusCode ?? response?.code ?? 0);
    const isSuccessful = statusCode === 200 || statusCode === 201 || response?.success === true;

    if (!isSuccessful) {
      setInfoMessage(response?.message || "Failed to update profile details.");
      return;
    }

    setDetailsForm((current) => ({
      ...current,
      ...mapProfileDetails(response?.data || {}, editChannelPartner),
    }));
    toast.success(response?.message || "Profile details updated successfully.");
  };

  const saveBankDetails = async (event) => {
    event.preventDefault();
    if (!bankForm.accountHolder.trim() || !bankForm.accountNumber.trim()) {
      setBankMessage("Account holder name and account number are required.");
      return;
    }

    if (!editChannelPartner?.id) {
      setBankMessage("Unable to save bank details. Missing channel partner ID.");
      return;
    }

    setIsSavingBank(true);
    setBankMessage("");

    const response = await apiCall({
      endpoint: hasBankDetails
        ? `/channel-partner/bank-details/${editChannelPartner.id}`
        : "/channel-partner/bank-details",
      method: hasBankDetails ? "PUT" : "POST",
      data: {
        channel_partner_id: Number(editChannelPartner.id),
        account_holder_name: bankForm.accountHolder.trim(),
        bank_name: bankForm.bankName.trim(),
        bank_branch: bankForm.bankBranch.trim(),
        account_number: bankForm.accountNumber.trim(),
        ifsc_code: bankForm.ifsc.trim(),
        upi_id: bankForm.upiId.trim(),
        bank_document: bankForm.bankDocument.trim(),
      },
    });

    setIsSavingBank(false);

    const statusCode = Number(response?.status ?? response?.statusCode ?? response?.code ?? 0);
    const isSuccessful = statusCode === 200 || statusCode === 201 || response?.success === true;

    if (!isSuccessful) {
      setBankMessage(response?.message || "Failed to save bank details.");
      return;
    }

    const nextBankDetails = mapBankDetails(response?.data || bankForm);
    setHasBankDetails(true);
    setBankForm(nextBankDetails);
    toast.success(response?.message || "Bank details saved successfully.");
  };

  if (!editChannelPartner?.id) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      <div className="mt-6 px-4 sm:px-6">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <button
                  type="button"
                  onClick={() => navigate("/channel-partner")}
                  className="mb-2 text-sm font-medium text-blue-600 hover:underline"
                >
                  Back to Channel Partner List
                </button>
                <h1 className="text-2xl font-semibold text-gray-900">Edit Channel Partner</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Uses channel-partner profile and bank APIs for this flow only.
                </p>
              </div>
              <div className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                Channel Partner Flow
              </div>
            </div>
          </div>

          <div className="space-y-6 px-6 py-6">
            {infoMessage && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                {infoMessage}
              </div>
            )}

            <form onSubmit={saveDetails} className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-gray-900">Profile Details</div>
                </div>
                {isLoadingProfile && <span className="text-xs text-gray-500">Loading profile...</span>}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={detailsForm.name}
                    onChange={updateDetailsField("name")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter name"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Company Name</label>
                  <input
                    type="text"
                    value={detailsForm.companyName}
                    onChange={updateDetailsField("companyName")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={detailsForm.email}
                    onChange={updateDetailsField("email")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter email"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Mobile</label>
                  <input
                    type="text"
                    value={detailsForm.mobile}
                    onChange={updateDetailsField("mobile")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter mobile"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Address</label>
                  <textarea
                    rows={3}
                    value={detailsForm.address}
                    onChange={updateDetailsField("address")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={detailsForm.status}
                      onChange={updateDetailsField("status")}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">FCM Token</label>
                    <input
                      type="text"
                      value={detailsForm.fcmToken}
                      onChange={updateDetailsField("fcmToken")}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      placeholder="Enter FCM token"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Email Verified</label>
                    <select
                      value={detailsForm.emailVerified}
                      onChange={updateDetailsField("emailVerified")}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    >
                      <option value="1">Verified</option>
                      <option value="0">Not Verified</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Mobile Verified</label>
                    <select
                      value={detailsForm.mobileVerified}
                      onChange={updateDetailsField("mobileVerified")}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    >
                      <option value="1">Verified</option>
                      <option value="0">Not Verified</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => navigate("/channel-partner")}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingProfile}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
                >
                  {isSavingProfile ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>

            <form onSubmit={saveBankDetails} className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-gray-900">Bank Details</div>
                </div>
                {isLoadingBank && <span className="text-xs text-gray-500">Loading bank details...</span>}
              </div>

              {bankMessage && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                  {bankMessage}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Account Holder Name</label>
                  <input
                    type="text"
                    value={bankForm.accountHolder}
                    onChange={updateBankField("accountHolder")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter account holder name"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Bank Name</label>
                  <input
                    type="text"
                    value={bankForm.bankName}
                    onChange={updateBankField("bankName")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter bank name"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Bank Branch</label>
                  <input
                    type="text"
                    value={bankForm.bankBranch}
                    onChange={updateBankField("bankBranch")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter bank branch"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Account Number</label>
                  <input
                    type="text"
                    value={bankForm.accountNumber}
                    onChange={updateBankField("accountNumber")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter account number"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">IFSC Code</label>
                  <input
                    type="text"
                    value={bankForm.ifsc}
                    onChange={updateBankField("ifsc")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter IFSC code"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">UPI ID</label>
                  <input
                    type="text"
                    value={bankForm.upiId}
                    onChange={updateBankField("upiId")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter UPI ID"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => navigate("/channel-partner")}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingBank}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
                >
                  {isSavingBank ? "Saving..." : "Save Bank Details"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChannelPartnerEdit;
